import * as React from 'react';

// The card is drawn in 3D: a disc of radius OUTER lifted LIFT above the base of its rim, seen from
// above-front. `project` maps a point of that disc (bearing, radius, height) to the screen
const INC = (50 * Math.PI) / 180;
const SIN = Math.sin(INC);
const COS = Math.cos(INC);
const OUTER = 42;
const LIFT = 8;
const PIVOT = 10;

const TICKS = Array.from({ length: 36 }, (_, i) => i * 10);
const DEGREES = TICKS.filter((angle) => angle % 30 === 0 && angle % 90 !== 0);
const CARDINALS = [
  { label: 'N', angle: 0 },
  { label: 'E', angle: 90 },
  { label: 'S', angle: 180 },
  { label: 'W', angle: 270 },
];

const project = (r: number, deg: number, y = 0): [number, number] => {
  const a = (deg * Math.PI) / 180;
  return [r * Math.sin(a), -r * Math.cos(a) * SIN - y * COS];
};

const points = (list: Array<[number, number]>): string => list.map(([x, y]) => `${x},${y}`).join(' ');

/** Sampled circle of the card, as a polygon */
const circle = (r: number, y: number): string => {
  const list: Array<[number, number]> = [];
  for (let deg = 0; deg < 360; deg += 3) {
    list.push(project(r, deg, y));
  }
  return points(list);
};

/** The near half of the rim, between the base and the dial, which is what makes the volume */
const wall = (): string => {
  const list: Array<[number, number]> = [];
  for (let deg = 90; deg <= 270; deg += 3) {
    list.push(project(OUTER, deg, LIFT));
  }
  for (let deg = 270; deg >= 90; deg -= 3) {
    list.push(project(OUTER, deg, 0));
  }
  return points(list);
};

/** Only the near half of the rim is visible: the rest is hidden behind the dial */
const nearSide = (bearing: number): boolean => {
  const a = ((bearing % 360) + 360) % 360;
  return a > 80 && a < 280;
};

/** One petal of the four-point needle, folded along its ridge so it reads as a raised star */
const petal = (
  rot: number, deg: number, len: number, light: string, dark: string,
  pivot: [number, number], ridge: Array<[number, number]>,
) => {
  const tip = project(len, deg + rot, 3);
  return [
    <polygon key={`${deg}L`} points={points([pivot, tip, ridge[0]])} fill={light} />,
    <polygon key={`${deg}D`} points={points([pivot, tip, ridge[1]])} fill={dark} />,
  ];
};

interface CompassProps {
  /** Diameter in px */
  size?: number;
}

interface CompassState {
  /** Degrees clockwise from true north, undefined until the sensor reports */
  heading?: number;
}

export default class Compass extends React.Component<CompassProps, CompassState> {
  state: CompassState = {};

  private eventName?: string;
  private handler?: EventListener;
  private lastUpdate = 0;

  componentDidMount() {
    // Same source as the map heading (see TreeMaps), adjusted for the screen rotation
    this.eventName = 'ondeviceorientationabsolute' in window ? 'deviceorientationabsolute' : 'deviceorientation';
    this.handler = ((event: DeviceOrientationEvent & { webkitCompassHeading?: number }) => {
      const raw = typeof event.webkitCompassHeading === 'number'
        ? event.webkitCompassHeading
        : event.alpha === null ? null : 360 - event.alpha;
      if (raw === null || !Number.isFinite(raw)) {
        return;
      }
      const screenAngle = window.screen.orientation?.angle ?? 0;
      const next = (raw + screenAngle + 360) % 360;
      const current = this.state.heading;
      const now = Date.now();
      // A still phone fires 60 times a second: redraw on 1 degree of movement, or every 100 ms
      const moved = current === undefined || Math.abs(((next - current + 540) % 360) - 180) >= 1;
      if (!moved && now - this.lastUpdate < 100) {
        return;
      }
      this.lastUpdate = now;
      this.setState({ heading: next });
    }) as EventListener;
    window.addEventListener(this.eventName, this.handler);
  }

  componentWillUnmount() {
    if (this.eventName && this.handler) {
      window.removeEventListener(this.eventName, this.handler);
    }
  }

  render() {
    const size = this.props.size ?? 120;
    const { heading } = this.state;
    // The card turns with the heading while the index above it stays put (marine convention).
    // Without a sensor the dial is shown greyed, still pointing north
    const rot = heading === undefined ? 0 : -heading;
    const pivot = project(0, 0, PIVOT);
    const ridge = [project(6, rot + 90, PIVOT * 0.55), project(6, rot - 90, PIVOT * 0.55)];

    return (
      <svg viewBox="-46 -42 92 84" width={size} height={size} role="img" aria-label="Compass">
        <defs>
          <linearGradient id="tw-compass-wall" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#2b2b2b" />
            <stop offset="1" stopColor="#0a0a0a" />
          </linearGradient>
          <linearGradient id="tw-compass-edge" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#fff" />
            <stop offset="0.55" stopColor="#fff" />
            <stop offset="0.56" stopColor="#cf1322" />
            <stop offset="1" stopColor="#cf1322" />
          </linearGradient>
        </defs>
        {/* Index of the direction the phone faces, outside the dial */}
        <polygon points="0,-41.5 -3.6,-35 3.6,-35" fill="#cf1322" />
        <g opacity={heading === undefined ? 0.45 : 1}>
          <polygon points={circle(OUTER, 0)} fill="#111" />
          <polygon points={wall()} fill="url(#tw-compass-wall)" />
          {/* Graduations and numbers stand on the rim wall, as on a marine compass */}
          {TICKS.map((angle) => {
            const bearing = angle + rot;
            if (!nearSide(bearing)) {
              return null;
            }
            const major = angle % 30 === 0;
            const [x1, y1] = project(OUTER, bearing, LIFT);
            const [x2, y2] = project(OUTER, bearing, major ? LIFT * 0.32 : LIFT * 0.56);
            return (
              <line
                key={angle}
                x1={x1} y1={y1} x2={x2} y2={y2}
                stroke={major ? '#fff' : '#9a9a9a'}
                strokeWidth={major ? 1.1 : 0.6}
              />
            );
          })}
          {DEGREES.map((angle) => {
            const bearing = angle + rot;
            if (!nearSide(bearing)) {
              return null;
            }
            const [x, y] = project(OUTER, bearing, LIFT * 0.58);
            return (
              <text key={angle} x={x} y={y + 1.8} textAnchor="middle" fontSize="5.6" fill="#e6e6e6">
                {angle}
              </text>
            );
          })}
          <polygon points={circle(OUTER, LIFT)} fill="#000" stroke="url(#tw-compass-edge)" strokeWidth="1.4" />
          {/* Red cross-hairs at the four cardinals */}
          {CARDINALS.map(({ angle }) => {
            const [x1, y1] = project(3, angle + rot, LIFT);
            const [x2, y2] = project(OUTER - 1.5, angle + rot, LIFT);
            return <line key={`x${angle}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#cf1322" strokeWidth="0.7" />;
          })}
          {CARDINALS.map(({ label, angle }) => {
            const [x, y] = project(OUTER - 10, angle + rot, LIFT);
            return (
              <text
                key={label}
                x={x} y={y + 3.2}
                textAnchor="middle" fontSize="10" fontWeight="bold"
                fill={label === 'N' ? '#cf1322' : '#fff'}
              >
                {label}
              </text>
            );
          })}
          {/* Four-point needle, red tipped, each petal folded along its ridge */}
          {petal(rot, 90, 17, '#e8e8e8', '#b0b0b0', pivot, ridge)}
          {petal(rot, 270, 17, '#e8e8e8', '#b0b0b0', pivot, ridge)}
          {petal(rot, 180, 30, '#f2f2f2', '#a8a8a8', pivot, ridge)}
          {petal(rot, 0, 30, '#f2f2f2', '#a8a8a8', pivot, ridge)}
          <polygon points={points([project(30, rot, 3), project(21.5, rot + 6, 6), project(21.5, rot - 6, 6)])} fill="#cf1322" />
          <polygon points={points([project(30, rot + 180, 3), project(21.5, rot + 186, 6), project(21.5, rot + 174, 6)])} fill="#cf1322" />
          <ellipse cx={pivot[0]} cy={pivot[1]} rx="4" ry={4 * SIN} fill="#444" />
          <circle cx={pivot[0]} cy={pivot[1]} r="1.6" fill="#eee" />
        </g>
      </svg>
    );
  }
}
